import { useTranslation } from 'react-i18next';
/**
 * Text and Annotation Tools
 * 
 * Provides text creation and annotation features:
 * - Single Line Text
 * - Multi-line Text (MText)
 * - Text Styles
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Html, Text } from '@react-three/drei';
import { useAppStore } from '../../state/store';
import { GROUND_PLANE, snapToGroundGrid } from '../../core/scene';
import { undoableAdd, undoableModify, makeEntity } from '../../core/commands/entityOps';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../../utils/settingsPersistence';
import { useAnnotationInk } from '../../hooks/useThemeInk';
import { reorderRtlText, firstStrongDirection } from '../../utils/text/bidi';
import { useAnnoMultiplier } from '../../state/annotationScaleStore';
import { renderFieldText, hasFieldCodes } from '../../core/commands/fields';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { useFieldCacheStore } from '../../core/commands/fields';

// ============================================================================
// TEXT STYLES
// ============================================================================

export interface TextStyle {
    id: string;
    name: string;
    fontFamily: string;
    fontSize: number;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
    color: string;
    alignment: 'left' | 'center' | 'right';
    lineHeight: number;
}

export interface TextStyleState {
    styles: Record<string, TextStyle>;
    activeStyleId: string;
    addStyle: (style: TextStyle) =>void; updateStyle: (id: string, updates: Partial<TextStyle>) => void;
    deleteStyle: (id: string) => void;
    setActiveStyle: (id: string) => void;
    getActiveStyle: () => TextStyle;
}

const defaultStyle: TextStyle = {
    id: 'standard',
    name: 'Standard',
    fontFamily: 'Arial, sans-serif',
    fontSize: 0.3,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#ffffff',
    alignment: 'left',
    lineHeight: 1.2,
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTextStyleStore = create<TextStyleState>()(persist((set, get) => ({
    styles: {
        standard: defaultStyle,
        title: {
            id: 'title',
            name: 'Title',
            fontFamily: 'Arial, sans-serif',
            fontSize: 0.5,
            fontWeight: 'bold',
            fontStyle: 'normal',
            color: '#ffffff',
            alignment: 'center',
            lineHeight: 1.3,
        },
        annotation: {
            id: 'annotation',
            name: 'Annotation',
            fontFamily: 'Arial, sans-serif',
            fontSize: 0.2,
            fontWeight: 'normal',
            fontStyle: 'normal',
            color: '#ffffff',
            alignment: 'left',
            lineHeight: 1.1,
        },
    },
    activeStyleId: 'standard',

    addStyle: (style) => set((state) => ({
        styles: { ...state.styles, [style.id]: style }
    })),

    updateStyle: (id, updates) => set((state) => ({
        styles: {
            ...state.styles,
            [id]: { ...state.styles[id], ...updates }
        }
    })),

    deleteStyle: (id) => set((state) => {
        if (id === 'standard') return state; // Can't delete default
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: _, ...rest } = state.styles;
        return {
            styles: rest,
            activeStyleId: state.activeStyleId === id ? 'standard' : state.activeStyleId
        };
    }),

    setActiveStyle: (id) => set({ activeStyleId: id }),

    getActiveStyle: () => {
        const state = get();
        return state.styles[state.activeStyleId] || defaultStyle;
    },
}), {
    name: 'yap-text-styles',
    storage: settingsStorage,
    partialize: (state) => ({ styles: state.styles, activeStyleId: state.activeStyleId }),
}));

// ============================================================================
// TEXT TOOL
// ============================================================================

export interface TextData {
    content: string;
    position: number[];
    styleId: string;
    rotation: number;
    width?: number;      // For multiline text wrapping
    multiline: boolean;
}

/**
 * Use Text Tool Hook
 * 
 * Click to place text, then type
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTextTool() {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [editPosition, setEditPosition] = useState<THREE.Vector3 | null>(null);
    const [textContent, setTextContent] = useState('');
    const [isMultiline, setIsMultiline] = useState(false);

    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const activeTool = useAppStore((s) => s.activeTool);
    const activeStyleId = useTextStyleStore((s) => s.activeStyleId);

    const { raycaster, pointer, camera } = useThree();

    const isActive = activeTool === 'text' || activeTool === 'mtext';

    const getGroundPoint = useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);

    const snap = useCallback((point: THREE.Vector3): THREE.Vector3 => {
        const gs = 0.25;
        // Slightly above the ground plane (z is the level axis in Z-up)
        return snapToGroundGrid(point, gs, 0.05);
    }, []);

    const completeText = useCallback(() => {
        if (textContent.trim() && editPosition) {
            undoableAdd([makeEntity(isMultiline ? 'mtext' : 'text', `Text-${Date.now()}`, {
                content: textContent,
                position: [editPosition.x, editPosition.y, editPosition.z],
                styleId: activeStyleId,
                rotation: 0,
                multiline: isMultiline,
            } as TextData as unknown as Record<string, unknown>, activeLayerId)]);
        }

        setIsEditing(false);
        setEditPosition(null);
        setTextContent('');
    }, [textContent, editPosition, isMultiline, activeLayerId, activeStyleId]);

    const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;

        if (isEditing) {
            // Complete current text
            completeText();
        } else {
            event.stopPropagation();
            const point = getGroundPoint();
            if (point) {
                setEditPosition(snap(point));
                setIsEditing(true);
                setIsMultiline(activeTool === 'mtext');
            }
        }
    }, [isActive, isEditing, completeText, getGroundPoint, snap, activeTool]);

    // Keyboard handlers
    useEffect(() => {
        if (!isEditing) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsEditing(false);
                setEditPosition(null);
                setTextContent('');
            } else if (e.key === 'Enter' && !isMultiline) {
                completeText();
            } else if (e.key === 'Enter' && isMultiline && e.ctrlKey) {
                completeText();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isEditing, isMultiline, completeText]);

    return {
        isActive,
        isEditing,
        editPosition,
        textContent,
        setTextContent,
        isMultiline,
        handlePointerDown,
        completeText,
    };
}

/**
 * Text Input Overlay Component
 */
export const TextInputOverlay: React.FC<{
    position: THREE.Vector3;
    value: string;
    onChange: (value: string) => void;
    onComplete: () => void;
    multiline?: boolean;
}> = ({ position, value, onChange, onComplete, multiline }) => {
    const { t } = useTranslation();
    const style = useTextStyleStore((s) =>s.getActiveStyle()); return (<Html position={position} center>
            <div className="text-input-overlay">
                {multiline ? (
                    <textarea
                        autoFocus
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={t('misc.enterTextCtrlEnterToFinish')}
                        className="text-input-overlay__textarea"
                        style={{
                            color: styleTextColor(style.color, 'var(--color-ink-viewport)'),
                            fontFamily: style.fontFamily,
                        }}
                    />
                ) : (
                    <input
                        type="text"
                        autoFocus
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && onComplete()}
                        placeholder={t('misc.enterText')}
                        className="text-input-overlay__input"
                        style={{
                            color: styleTextColor(style.color, 'var(--color-ink-viewport)'),
                            fontFamily: style.fontFamily,
                        }}
                    />
                )}
                <div className="text-input-overlay__actions">
                    <button
                        onClick={onComplete}
                        className="text-input-overlay__button"
                    >{t('misc.oK')}</button>
                </div>
            </div>
        </Html>
    );
};

/**
 * Text Entity Renderer
 *
 * MTEXT formatting-code decoding (2D parity): \P → newline, {} braces
 * and the remaining inline codes (\H, \W, \Q, \A, \C, \T, \L, \l, \O, \P…)
 * are stripped so content renders as typed text. Font-switch codes capture
 * the family name for the text material.
 */
export function decodeMText(raw: string): { text: string; fontFamily?: string } {
    if (!raw) return { text: '' };
    let fontFamily: string | undefined;
    const out = raw
        .replace(/\\P/g, '\n')
        .replace(/\\[HhWwQqAaTt](\+?-?[\d.]+)(x|X|d)?;?/g, '')
        .replace(/\\[Ff]([^;]*)/g, (_m, f: string) => { fontFamily = (f.split('|')[0] || '').trim() || fontFamily; return ''; })
        .replace(/\\[LlOoKk~C]/g, '')
        .replace(/\\[{}\\]/g, (m) => (m === '\\\\' ? '\\' : m.slice(1)))
        .replace(/[{}]/g, '');
    return { text: out, fontFamily };
}

/**
 * In-place text editor (DDEDIT / TEXTED=0 parity): double-clicking a placed
 * TEXT/MTEXT opens an anchored overlay at the text position; Enter (single)
 * or Ctrl+Enter (multiline) commits ONE undoable content change, Esc cancels.
 */
export const InPlaceTextEditor: React.FC<{
    entityId: string;
    onClose: () => void;
}> = ({ entityId, onClose }) => {
    const { t } = useTranslation();
    const entity = useAppStore((s) => s.entities[entityId]);
    const styleId = entity?.data?.styleId as string | undefined;
    const styles = useTextStyleStore((s) => s.styles);
    const style = styles[styleId ?? ''] ?? styles['standard'];
    const multiline = !!entity?.data?.multiline;
    const [value, setValue] = useState(() => String(entity?.data?.content ?? ''));
    // RTL parity (Faz 8.5): the input box mirrors when the first strong
    // directional character is RTL (display-only — stored text stays logical).
    const direction = firstStrongDirection(value);

    if (!entity) return null;
    const pos = (entity.data.position as number[]) ?? [0, 0, 0];

    const commit = () => {
        if (value !== String(entity.data.content ?? '')) {
            undoableModify([entityId], (d) => ({ ...d, content: value }));
        }
        onClose();
    };

    return (
        <group position={[pos[0], pos[1], (pos[2] ?? 0) + 0.05]}>
            <Html center>
                <div className="text-input-overlay">
                    {multiline ? (
                        <textarea
                            autoFocus
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Escape') { e.preventDefault(); onClose(); }
                                if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); commit(); }
                            }}
                            className="text-input-overlay__textarea text-input-overlay__textarea--wide"
                            style={{
                                color: styleTextColor(style?.color, 'var(--color-ink-viewport)'),
                                fontFamily: style.fontFamily,
                                direction,
                            }}
                        />
                    ) : (
                        <input
                            type="text"
                            autoFocus
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter') { e.preventDefault(); commit(); }
                                if (e.key === 'Escape') { e.preventDefault(); onClose(); }
                            }}
                            className="text-input-overlay__input"
                            style={{
                                color: styleTextColor(style?.color, 'var(--color-ink-viewport)'),
                                fontFamily: style.fontFamily,
                                direction,
                            }}
                        />
                    )}
                    <div className="text-input-overlay__actions">
                        <button onClick={commit} className="text-input-overlay__button">{t('misc.oK')}</button>
                        <button onClick={onClose} className="text-input-overlay__button">{t('misc.cancel')}</button>
                    </div>
                </div>
            </Html>
        </group>
    );
};

/**
 * Resolve a text style's stored color to paint color. Styles shipped before
 * theming default to '#ffffff' (and persisted stores carry it; the legacy
 * 'Annotation' style shipped '#aaaaaa') — treat those defaults as "unset"
 * and follow the viewport ink; the DOM overlays pass the CSS custom property
 * as the ink value.
 */
export const styleTextColor = (styleColor: string | undefined, ink: string): string =>
    !styleColor || styleColor === '#ffffff' || styleColor === '#aaaaaa' ? ink : styleColor;

export const TextEntity: React.FC<{
    data: TextData;
    entityId?: string;
    isSelected: boolean;
    isHovered: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void; onDoubleClick?: (e: ThreeEvent<MouseEvent>) => void;
}> = ({ data, entityId, isSelected, isHovered, onClick, onDoubleClick }) => {
    const ink = useAnnotationInk();
    const styles = useTextStyleStore((s) => s.styles);
    const style = styles[data.styleId] || styles['standard'];
    const color = isSelected ? '#00aaff' : isHovered ? '#ffaa00' : styleTextColor(style?.color, ink);
    // Annotative size multiplier (Faz 8.4): annotative text scales with the
    // current annotation scale relative to its native scale.
    const multiplier = useAnnoMultiplier(data as unknown as Record<string, any>);
    const decoded = decodeMText(String(data.content ?? ''));
    // Live FIELD values (Faz 8.8): %<…>% codes substitute their cached
    // resolved value; FIELDDISPLAY gates the grey-box indicator. The cache
    // store subscription re-renders the host when a trigger refreshes it.
    const isFieldHost = hasFieldCodes(String(data.content ?? ''));
    const fieldDisplay = useSystemVarsStore((s) => s.FIELDDISPLAY) !== 0;
    const fieldVersion = useFieldCacheStore((s) => s.values);
    void fieldDisplay; void fieldVersion;
    const content = isFieldHost && entityId
        ? renderFieldText(decoded.text, entityId)
        : decoded.text;
    // RTL visual order (Faz 8.5): word-run reordering + LRM locks happen
    // at render time only — the stored content stays in logical order.
    const display = reorderRtlText(content);
    return (<group
            position={[data.position[0], data.position[1], data.position[2]]}
            // Z-up convention: text lies in the XY ground plane, heading is
            // rotation around the level (Z) axis. drei Text already faces +Z.
            rotation={[0, 0, data.rotation]}
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
            onDoubleClick={(e) => { if (onDoubleClick) { e.stopPropagation(); onDoubleClick(e); } }}
        >
            <Text
                color={color}
                fontSize={style.fontSize * multiplier}
                maxWidth={data.width || 10}
                lineHeight={style.lineHeight}
                textAlign={style.alignment}
                anchorX={style.alignment}
                anchorY="top"
            >
                {display}
            </Text>

            {/* Selection indicator */}
            {isSelected && (
                <mesh>
                    <planeGeometry args={[
                        Math.max(data.content.length * style.fontSize * 0.6, 1),
                        style.fontSize * 1.5
                    ]} />
                    <meshBasicMaterial
                        color="#00aaff"
                        transparent
                        opacity={0.1}
                    />
                </mesh>
            )}
        </group>
    );
};
